import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectField, TextInputField } from '@brighte/form-components';
import { EmploymentType, IncomeVerificationType } from '@brighte/validation';
import { AssistantFormFields, useAssistantSafe } from '@components/Assistant';
import { LABELS, PLACEHOLDER } from '@utils';
import ABNInput from './ABNInput';
import RetiredOrPensioner from './RetiredOrPensioner';
import { getIncomeOptions } from './helpers';

type EmploymentFieldsProps = {
  financialProductId: string;
};

// The field path is also how the assistant identifies the question, so declare
// it once and use it for both.
const EMPLOYMENT_TYPE_FIELD = 'applicant.employment.type' as const;

function EmploymentFields({ financialProductId }: EmploymentFieldsProps) {
  const { control, setValue, register } = useFormContext<IncomeVerificationType>();
  const applicantIncomeType = useWatch({ control, name: EMPLOYMENT_TYPE_FIELD });

  // The step does not save until the customer continues, so until then the
  // assistant's server-side snapshot has no employment type and its document
  // rules ask how they earn their income — a question this field just answered.
  // Report the selection (a category, never a personal value) so it can see it.
  // Destructured because the context object is re-memoised on every journal
  // change, while this callback is stable.
  const reportDraftSelections = useAssistantSafe()?.reportDraftSelections;
  useEffect(() => {
    reportDraftSelections?.({ [EMPLOYMENT_TYPE_FIELD]: applicantIncomeType });
    // A question that has left the screen is no longer an answer. React runs this
    // cleanup before the next effect, so a changed value retracts then re-sets;
    // on unmount it just retracts.
    return () => reportDraftSelections?.({ [EMPLOYMENT_TYPE_FIELD]: undefined });
  }, [applicantIncomeType, reportDraftSelections]);

  const resetEmploymentFields = () => {
    if (applicantIncomeType) {
      setValue(
        `applicant.employment.${applicantIncomeType === 'self-employed' ? 'selfEmployed' : applicantIncomeType}`,
        undefined,
      );
    }
  };

  const employmentFields = {
    [EmploymentType.EMPLOYED]: (
      <TextInputField
        name="applicant.employment.employed.employerBusinessName"
        control={control}
        label={LABELS.EMPLOYER_NAME}
      />
    ),
    [EmploymentType.SELF_EMPLOYED]: <ABNInput control={control} setValue={setValue} register={register} />,
    [EmploymentType.PENSIONER]: <RetiredOrPensioner control={control} />,
    [EmploymentType.OTHER]: null,
  };

  return (
    <>
      <AssistantFormFields fields={[EMPLOYMENT_TYPE_FIELD]} />
      {applicantIncomeType === EmploymentType.EMPLOYED && (
        <AssistantFormFields fields={['applicant.employment.employed.employerBusinessName']} />
      )}
      {applicantIncomeType === EmploymentType.SELF_EMPLOYED && (
        <AssistantFormFields
          fields={['applicant.employment.selfEmployed.abn', 'applicant.employment.selfEmployed.entityName']}
        />
      )}
      <SelectField
        name={EMPLOYMENT_TYPE_FIELD}
        control={control}
        label={LABELS.INCOME_TYPE}
        options={getIncomeOptions(financialProductId)}
        placeholder={PLACEHOLDER}
        onChange={resetEmploymentFields}
      />
      <>{employmentFields[applicantIncomeType] ?? null}</>
    </>
  );
}

export default EmploymentFields;
